import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Search, Filter, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { adminAPI } from '../../lib/api';
import { formatPrice, formatDateTime, ORDER_STATUS } from '../../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadOrders();
  }, [page, statusFilter]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await adminAPI.getOrders(params);
      setOrders(response.data.items);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((order) => {
      const orderNumber = order.order_number?.toLowerCase() || '';
      const email = order.email?.toLowerCase() || '';
      const fullName = `${order.shipping_address?.first_name || ''} ${order.shipping_address?.last_name || ''}`.toLowerCase();
      return orderNumber.includes(term) || email.includes(term) || fullName.includes(term);
    });
  }, [orders, searchTerm]);

  const handleAssignToMe = async (orderId) => {
    try {
      await adminAPI.assignOrderToMe(orderId);
      toast.success('Commande assignée à vous');
      loadOrders();
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const handleQuickStatusUpdate = async (orderId, newStatus) => {
    setUpdatingStatusId(orderId);
    try {
      await adminAPI.updateOrder(orderId, { status: newStatus });
      toast.success('Statut mis à jour');
      loadOrders();
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleExportCSV = () => {
    if (!filteredOrders.length) return;

    const headers = [
      'Numéro',
      'Email',
      'Prénom',
      'Nom',
      'Adresse',
      'Ville',
      'Code Postal',
      'Pays',
      'Téléphone',
      'Date',
      'Statut',
      'Total (€)',
      'Livraison (€)',
      'Réduction (€)',
      'Articles',
      'Notes internes'
    ];

    const rows = filteredOrders.map((order) => {
      const shipping = order.shipping_address || {};
      const formatValue = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value).replace(/"/g, '""');
        return `"${str}"`;
      };

      return [
        order.order_number,
        order.email,
        shipping.first_name || '',
        shipping.last_name || '',
        `${shipping.address_line1 || ''} ${shipping.address_line2 || ''}`.trim(),
        shipping.city || '',
        shipping.postal_code || '',
        shipping.country || '',
        shipping.phone || '',
        formatDateTime(order.created_at),
        ORDER_STATUS[order.status]?.label || order.status,
        (order.total_cents / 100).toFixed(2),
        (order.shipping_cents / 100).toFixed(2),
        (order.discount_cents / 100).toFixed(2),
        order.items?.length || 0,
        order.admin_notes || '',
      ].map(formatValue).join(';');
    });

    const csvContent = ['\uFEFF' + headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `commandes_vyastore_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Commandes</h1>
          <p className="text-muted-foreground">Gérez toutes les commandes</p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={!filteredOrders.length}
        >
          <Download className="h-4 w-4 mr-2" />
          Exporter CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Rechercher par numéro, email ou nom..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(ORDER_STATUS).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Commande</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Conseiller</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-24" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-32" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-24" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-20" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16 ml-auto" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-8 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Aucune commande trouvée
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order, index) => {
                  const status = ORDER_STATUS[order.status] || ORDER_STATUS.PENDING_PAYMENT;
                  return (
                    <motion.tr
                      key={order.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="table-row-hover"
                    >
                      <TableCell>
                        <span className="font-mono text-sm">{order.order_number}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                          </p>
                          <p className="text-sm text-muted-foreground">{order.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {order.assigned_to ? (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-primary">
                                {(order.assigned_to_name || 'A').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-sm">{order.assigned_to_name || 'Assigné'}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{formatDateTime(order.created_at)}</span>
                      </TableCell>
                      <TableCell className="min-w-[200px] flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          <Badge className={status.color}>{status.label}</Badge>
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleQuickStatusUpdate(order.id, value)}
                            disabled={updatingStatusId === order.id}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ORDER_STATUS).map(([key, { label }]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPrice(order.total_cents)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!order.assigned_to && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAssignToMe(order.id)}
                              title="M'assigner"
                            >
                              Assigner
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/orders/${order.id}`} data-testid={`view-order-${order.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </motion.tr>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} sur {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}
